import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { Skeleton } from '@ordo/ui/skeleton'
import { Switch } from '@ordo/ui/switch'
import { Textarea } from '@ordo/ui/textarea'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import {
  type SubscriptionTopic,
  useCreateSubscriptionTopic,
  useDeleteSubscriptionTopic,
  useSubscriptionTopics,
  useUpdateSubscriptionTopic,
} from 'hooks/api'
import { Plus, Save, Trash2 } from 'lucide-react'
import { useCallback, useState } from 'react'

export function SubscriptionTopicsSection() {
  const { data: topics, loading, refetch } = useSubscriptionTopics({ includeInactive: true })
  const { perform: create, loading: creating } = useCreateSubscriptionTopic()
  const { perform: update } = useUpdateSubscriptionTopic()
  const { perform: remove } = useDeleteSubscriptionTopic()

  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) {
      return
    }
    await create({
      name: newName.trim(),
      description: newDescription.trim() || null,
      isDefault: (topics?.length ?? 0) === 0,
    })
    setNewName('')
    setNewDescription('')
    refetch()
  }, [newName, newDescription, create, refetch, topics])

  const handleSetDefault = useCallback(
    async (topic: SubscriptionTopic) => {
      await update(topic.id, { isDefault: true })
      refetch()
    },
    [update, refetch],
  )

  const handleDelete = useCallback(
    async (topic: SubscriptionTopic) => {
      if (topic.isDefault) {
        return
      }
      await remove(topic.id)
      refetch()
    },
    [remove, refetch],
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription Topics</CardTitle>
        <CardDescription>
          Group scheduled communications by topic so recipients can opt out of one list without losing transactional
          messages. Exactly one topic is marked default — scheduled communications without an explicit topic fall back
          to it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <LoadingWrapper loading={loading} loadingElement={<Skeleton className="h-24 w-full" />}>
          {topics?.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No topics yet. Create one below — it will become the workspace default.
            </p>
          ) : null}
          {(topics ?? []).map((topic) => (
            <div key={topic.id} className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{topic.name}</span>
                  {topic.isDefault && <Badge variant="secondary">Default</Badge>}
                  {!topic.active && <Badge variant="outline">Inactive</Badge>}
                </div>
                {topic.description && <p className="mt-1 text-sm text-muted-foreground">{topic.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                {!topic.isDefault && topic.active && (
                  <Button variant="outline" size="sm" onClick={() => handleSetDefault(topic)}>
                    Make default
                  </Button>
                )}
                {!topic.isDefault && (
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(topic)} title="Deactivate">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </LoadingWrapper>

        <div className="rounded-md border p-3">
          <div className="mb-2 text-sm font-medium">Add topic</div>
          <div className="grid gap-2">
            <div>
              <Label htmlFor="new-topic-name">Name</Label>
              <Input
                id="new-topic-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Newsletter, Product updates, ..."
              />
            </div>
            <div>
              <Label htmlFor="new-topic-description">Description (optional)</Label>
              <Textarea
                id="new-topic-description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Shown on the preferences page to help recipients choose."
                rows={2}
              />
            </div>
            <div>
              <Button size="sm" onClick={handleCreate} disabled={creating || !newName.trim()}>
                <Plus className="mr-1 h-3 w-3" />
                Create topic
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Compact row editor for the description field inline (unused by the main
// flow but exported for potential future use).
export function SubscriptionTopicRow({ topic, onSaved }: { topic: SubscriptionTopic; onSaved: () => void }) {
  const [description, setDescription] = useState(topic.description ?? '')
  const [active, setActive] = useState(topic.active)
  const { perform: update, loading } = useUpdateSubscriptionTopic()

  const handleSave = async () => {
    await update(topic.id, { description: description || null, active })
    onSaved()
  }

  return (
    <div className="flex items-center gap-2">
      <Input value={description} onChange={(e) => setDescription(e.target.value)} />
      <Switch checked={active} onCheckedChange={setActive} />
      <Button size="sm" onClick={handleSave} disabled={loading}>
        <Save className="h-3 w-3" />
      </Button>
    </div>
  )
}
